import { useEffect, useState, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import './GameViewport.css';

/**
 * GameViewport - Scale-to-fit viewport container (same behaviour on web & mobile)
 *
 * MOBILE (native / ?mobile=true):  2160x1080 (18:9) reference + 1.0× zoom
 * WEB:                             2160x1080 (18:9) reference + 1.1× zoom
 *
 * Both modes:
 *  - Render the game at the reference resolution
 *  - Scale + centre to fill any screen (letterbox bars on unused sides)
 *  - CSS transform creates a containing block for position:fixed children
 *    so panels/buttons never drift to screen corners on large displays
 */

const MOBILE_WIDTH  = 2160;
const MOBILE_HEIGHT = 1080;
const MOBILE_ZOOM   = 1.0;

const WEB_WIDTH  = 2160;   // same reference width as mobile
const WEB_HEIGHT = 1080;
const WEB_ZOOM   = 1.1;

const GameViewport = ({ children, zoom: zoomProp }) => {
  const containerRef = useRef(null);
  const [scale,  setScale]  = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  const mobilePreview = new URLSearchParams(window.location.search).get('mobile') === 'true';
  const isNative = Capacitor.isNativePlatform() || mobilePreview;

  const refWidth  = isNative ? MOBILE_WIDTH  : WEB_WIDTH;
  const refHeight = isNative ? MOBILE_HEIGHT : WEB_HEIGHT;
  const zoom      = zoomProp !== undefined ? zoomProp : (isNative ? MOBILE_ZOOM : WEB_ZOOM);

  useEffect(() => {
    const calculateScale = () => {
      const screenWidth  = window.innerWidth;
      const screenHeight = window.innerHeight;

      // Fit the zoomed canvas within the screen without overflow.
      // Compute the largest scale where (refW × scale) ≤ screenW
      // AND (refH × scale) ≤ screenH, with zoom already factored in.
      //
      // Previous approach multiplied newScale by zoom AFTER fitting to
      // one axis, which caused the canvas to overflow the other axis at
      // common resolutions (e.g. 1920×1080 → 2110px wide, 190px cut off).
      const scaleToFit = Math.min(
        screenWidth  / (refWidth  * zoom),
        screenHeight / (refHeight * zoom)
      );
      const newScale = scaleToFit * zoom;

      // Centre the canvas; positive offset = letterbox bar on that axis.
      const offsetX = (screenWidth  - refWidth  * newScale) / 2;
      const offsetY = (screenHeight - refHeight * newScale) / 2;

      setScale(newScale);
      setOffset({ x: offsetX, y: offsetY });
    };

    calculateScale();
    window.addEventListener('resize', calculateScale);
    window.addEventListener('orientationchange', calculateScale);
    return () => {
      window.removeEventListener('resize', calculateScale);
      window.removeEventListener('orientationchange', calculateScale);
    };
  }, [isNative, refWidth, refHeight, zoom, zoomProp]);

  // ── Mobile ───────────────────────────────────────────────────────────────
  if (isNative) {
    return (
      <div className="game-viewport-wrapper">
        <div
          ref={containerRef}
          className="game-viewport"
          style={{
            width: `${MOBILE_WIDTH}px`,
            height: `${MOBILE_HEIGHT}px`,
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
            transformOrigin: 'top left'
          }}
        >
          {children}
        </div>
      </div>
    );
  }

  // ── Web: reuse game-viewport classes so all mobile CSS overrides apply ────
  // This guarantees position:fixed elements are anchored via calc(50%-X)
  // formulas (same as mobile) and never drift on large screens.
  return (
    <div className="game-viewport-wrapper web-mode">
      <div
        ref={containerRef}
        className="game-viewport"
        style={{
          width: `${WEB_WIDTH}px`,
          height: `${WEB_HEIGHT}px`,
          transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
          transformOrigin: 'top left'
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default GameViewport;
